import {
  CustomToolHttpMethod,
  CustomToolMockResponseType,
  defaultCustomToolTimeoutMs,
  extractCustomToolPathPlaceholders,
  type CustomToolParameter,
  type CustomToolPathParameter,
} from "@repo/app-types/entities/tools/custom";
import {
  KeyValuePairList,
  type KeyValuePair,
} from "@repo/ui/FormLists/keyValuePairList";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { Label } from "@repo/ui/Label/label";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { useMemo, type FC } from "react";
import { CustomToolParameterFormBuilder } from "./customToolParameterFormBuilder.js";
import { SharedToolFormFields } from "./sharedToolFormFields.js";
import { SigningKeyDisplay } from "./signingKeyDisplay.js";

const defaultCustomToolTimeoutMsString = defaultCustomToolTimeoutMs.toString();

export const httpMethodOptions: SimpleSelectOption<CustomToolHttpMethod>[] = [
  { value: CustomToolHttpMethod.GET, label: "GET" },
  { value: CustomToolHttpMethod.POST, label: "POST" },
  { value: CustomToolHttpMethod.PUT, label: "PUT" },
  { value: CustomToolHttpMethod.PATCH, label: "PATCH" },
];

const mockResponseTypeOptions: SimpleSelectOption<CustomToolMockResponseType>[] =
  [
    { value: CustomToolMockResponseType.STRING, label: "String" },
    { value: CustomToolMockResponseType.JSON, label: "JSON" },
  ];

interface CustomToolFormProps {
  name: string;
  description: string;
  settings: CustomToolFormSettings;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onSettingsChange: (settings: Partial<CustomToolFormSettings>) => void;
  isEditMode: boolean;
}

// Internal settings representation for form state
export interface CustomToolFormSettings {
  method: CustomToolHttpMethod;
  url: string;
  timeout: string;
  timeoutError: string | undefined;
  urlError: string | undefined;
  headers: readonly KeyValuePair[];
  queryParams: readonly KeyValuePair[];
  parameters: readonly CustomToolParameter[];
  pathParameters: readonly CustomToolPathParameter[];
  mockResponse: string;
  mockResponseType: CustomToolMockResponseType;
  mockResponseError: string | undefined;
}

export const CustomToolForm: FC<CustomToolFormProps> = ({
  name,
  description,
  settings,
  onNameChange,
  onDescriptionChange,
  onSettingsChange,
  isEditMode,
}) => {
  const pathPlaceholders = useMemo(
    () => extractCustomToolPathPlaceholders(settings.url),
    [settings.url],
  );

  const handlePathParameterChange = (
    name: string,
    description: string,
  ): void => {
    onSettingsChange({
      pathParameters: [
        ...settings.pathParameters.filter(
          (pathParameter) => pathParameter.name !== name,
        ),
        { name, description },
      ],
      urlError: undefined,
    });
  };

  return (
    <>
      {isEditMode && <SigningKeyDisplay />}

      <SharedToolFormFields
        name={name}
        description={description}
        onNameChange={onNameChange}
        onDescriptionChange={onDescriptionChange}
        namePlaceholder="Fetch customer information"
        descriptionPlaceholder="This tool looks up the customer's plan and account history. The agent should use this if the caller asks about their current plan or billing."
      />

      <div>
        <Label size={Size.SMALL}>API endpoint</Label>
        <div className="mt-2 flex gap-2">
          <div className="w-28">
            <SimpleSelect
              options={httpMethodOptions}
              value={settings.method}
              onChange={(value) => onSettingsChange({ method: value })}
              isLabelHidden
              label="HTTP method"
            />
          </div>
          <div className="flex-1">
            <Input
              id="customUrl"
              value={settings.url}
              onChange={(e) =>
                onSettingsChange({ url: e.target.value, urlError: undefined })
              }
              placeholder="https://api.example.com/endpoint"
              description="Write a path segment as :name to have the agent fill it in, for example https://api.example.com/customers/:customer_id/orders."
              errorDescription={settings.urlError}
              maxLength={2048}
            />
          </div>
        </div>
      </div>

      {pathPlaceholders.length > 0 && (
        <div className="flex flex-col space-y-3">
          <Label size={Size.SMALL}>Path placeholders</Label>
          <p className="text-sm text-muted-foreground">
            Tell the agent what to put in each placeholder in the URL path.
          </p>

          {pathPlaceholders.map((placeholder) => (
            <Input
              key={placeholder}
              id={`customPathParameter-${placeholder}`}
              label={`:${placeholder}`}
              value={
                settings.pathParameters.find(
                  (pathParameter) => pathParameter.name === placeholder,
                )?.description ?? ""
              }
              onChange={(e) =>
                handlePathParameterChange(placeholder, e.target.value)
              }
              placeholder="The customer's account number, as stated by the caller"
              maxLength={2048}
            />
          ))}
        </div>
      )}

      <Input
        id="customTimeout"
        label="Timeout (ms)"
        value={settings.timeout}
        onChange={(e) =>
          onSettingsChange({ timeout: e.target.value, timeoutError: undefined })
        }
        placeholder={defaultCustomToolTimeoutMsString}
        errorDescription={settings.timeoutError}
        maxLength={100}
      />

      <KeyValuePairList
        label="Headers"
        description="Specify the HTTP headers required for your API request."
        value={settings.headers}
        onChange={(value) => onSettingsChange({ headers: value })}
        addButtonLabel="Add header"
        lengthOptions={{
          keyMinLength: 1,
          keyMaxLength: 512,
          valueMinLength: 1,
          valueMaxLength: 4096,
        }}
      />

      <KeyValuePairList
        label="Query parameters"
        description="Query string parameters to append to the URL."
        value={settings.queryParams}
        onChange={(value) => onSettingsChange({ queryParams: value })}
        addButtonLabel="Add parameter"
        lengthOptions={{
          keyMinLength: 1,
          keyMaxLength: 128,
          valueMinLength: 1,
          valueMaxLength: 256,
        }}
      />

      <CustomToolParameterFormBuilder
        label="Parameters"
        description="Define the parameters that the agent should provide in the request body to your API endpoint."
        value={settings.parameters}
        onChange={(value) => onSettingsChange({ parameters: value })}
      />

      <div>
        <Label size={Size.SMALL}>Mock response (optional)</Label>
        <p className="mt-1 text-sm text-muted-foreground">
          Response to return when this tool is called during a simulation. If
          not provided, a default simulation message will be used.
        </p>
        <div className="mt-2 flex gap-2">
          <div className="w-28">
            <SimpleSelect
              options={mockResponseTypeOptions}
              value={settings.mockResponseType}
              onChange={(value) =>
                onSettingsChange({ mockResponseType: value })
              }
              isLabelHidden
              label="Response type"
            />
          </div>
          <div className="flex-1">
            <Textarea
              id="mockResponse"
              value={settings.mockResponse}
              onChange={(value) =>
                onSettingsChange({
                  mockResponse: value,
                  mockResponseError: undefined,
                })
              }
              placeholder={
                settings.mockResponseType === CustomToolMockResponseType.JSON
                  ? '{"status": "success", "data": {...}}'
                  : "Tool executed successfully"
              }
              errorDescription={settings.mockResponseError}
            />
          </div>
        </div>
      </div>
    </>
  );
};
